import { startOfDay } from "date-fns/startOfDay";
import { storeToRefs } from "pinia";
import type { QInfiniteScroll } from "quasar";
import type { MaybeRef, Ref } from "vue";
import { computed, ref, unref, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import {
  DAY_IN_SECONDS,
  defaultMentionsFields,
  socialAutoRefreshEnabled,
  streamTypes,
} from "shared/constants";
import { dateToTimestamp, subtractTime } from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";
import type { MediumName } from "shared/helpers/media";
import {
  compositeIdToMention,
  SortOptionField,
  SortOptionMissing,
  SortOptionOrder,
} from "shared/helpers/mentions";
import { isSocialStream } from "shared/helpers/stream";
import type {
  StreamFiltersSortOption,
  StreamRequestFilters,
  StreamRequestFiltersSortOption,
} from "shared/helpers/StreamFilters";
import { isAbortError } from "shared/services/api/isAbortError";
import { fetchMentions } from "shared/services/fetch/fetchMentions";
import { type FetchService } from "shared/services/fetch/FetchServiceFactory";
import { useGlobalStore } from "shared/stores/global";
import { useMentionCountsStore } from "shared/stores/mentionCounts";
import { useSyndicationStore } from "shared/stores/syndication";
import { useUserStore } from "shared/stores/user";
import type { Nullable, Stream } from "shared/types";
import type { Mention, SyndicationKey } from "shared/types/mentions";

export const AUTO_REFRESH_MENTIONS_LIMIT = 3;

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_NUMBER_OF_PAGES = 1;
const REQUEST_TIMEOUT_MS = 90000;

export interface RequestOptions {
  allFields?: boolean;
  allowFutureContent?: boolean;
  defaultRange?: MaybeRef<DateRange | undefined>;
  fetchingOptions?: MaybeRef<MentionsOptions | undefined>;
  fetchingService?: MaybeRef<FetchService>;
  fields?: string;
  includeBookmarkSyndication?: boolean;
  infiniteScroll?: Ref<InstanceType<typeof QInfiniteScroll> | undefined>;
  missing?: SortOptionMissing;
  orderBy?: SortOptionOrder;
  page?: number;
  pageSize?: number;
  paginate?: boolean;
  sortBy?: SortOptionField;
  stream: MaybeRef<Stream>;
  transformMentions?: (mentions: Mention[]) => Mention[];
  useMilliseconds?: boolean;
}

export interface MentionsOptions {
  allFields?: boolean;
  allowFutureContent?: false;
  clearMentionsCount?: boolean;
  documentIds?: SyndicationKey[];
  endpoint?: string;
  excludedMedia?: MediumName[];
  fields?: string;
  filterSetId?: number;
  forceReload?: boolean;
  includeBookmarkSyndication?: boolean;
  keepAlreadyLoadedMentions?: boolean;
  limit?: number;
  loadLatestMentions?: boolean;
  missing?: SortOptionMissing;
  orderBy?: SortOptionOrder;
  page?: number;
  pageSize?: number;
  paginate?: boolean;
  range?: DateRange;
  search?: StreamRequestFilters;
  shouldTrackUniversalSearch?: boolean;
  silent?: boolean;
  sort_options?: StreamRequestFiltersSortOption[];
  sortBy?: SortOptionField;
  sortingOptions?: StreamFiltersSortOption[];
  source_group_keys?: string[];
  source_keys?: string[];
  stream_ids?: number[];
  transformMentions?: (mentions: Mention[]) => Mention[];
  useMilliseconds?: boolean;
}

export interface RequestParams extends MentionsOptions {
  range: DateRange;
  syndicationKeys: string[];
  timeout: number;
}

export default function useRequestMentions(options: RequestOptions) {
  const allFields = computed(() => unref(options.allFields || false));

  const allowFutureContent = computed(() =>
    unref(options.allowFutureContent || false)
  );

  const defaultRange = computed<DateRange>(
    () => unref(options.defaultRange) ?? DateRange.lastThirtyDays()
  );

  const fetchingOptions = computed<MentionsOptions>(
    () => unref(options.fetchingOptions) ?? {}
  );

  const fetchingService = computed<FetchService>(
    () => unref(options.fetchingService) ?? fetchMentions
  );

  const fields = computed(() => unref(options.fields || defaultMentionsFields));

  const includeBookmarkSyndication = computed(() =>
    unref(options.includeBookmarkSyndication || false)
  );

  const { infiniteScroll = ref() } = options;

  const missing = computed(() =>
    unref(options.missing || SortOptionMissing.LAST)
  );

  const orderBy = computed(() =>
    unref(options.orderBy || SortOptionOrder.DESC)
  );

  const page = ref(options.page || DEFAULT_PAGE);
  const pageSize = ref(options.pageSize || DEFAULT_PAGE_SIZE);

  const paginate = computed(() => unref(options.paginate || false));

  const sortBy = computed(() =>
    unref(options.sortBy || SortOptionField.TIMESTAMP)
  );

  const stream = computed<Stream>(() => unref(options.stream));

  const transformMentions = computed(() => {
    if (options.transformMentions) {
      unref(options.transformMentions);
    }

    return (mentions: Mention[]) => mentions;
  });

  const useMilliseconds = computed(() =>
    unref(options.useMilliseconds || true)
  );

  const globalStore = useGlobalStore();
  const { getContentDateCap } = storeToRefs(globalStore);
  const { getLowestSearchableDate } = globalStore;

  const defaultRequestOptions = computed<MentionsOptions>(() => ({
    allFields: allFields.value,
    fields: fields.value,
    paginate: paginate.value,
    sortingOptions: [
      {
        missing: missing.value,
        orderBy: orderBy.value,
        sortBy: sortBy.value,
      },
    ],
    transformMentions: transformMentions.value,
    useMilliseconds: useMilliseconds.value,
    ...fetchingOptions.value,
  }));

  const requestFilters = ref<StreamRequestFilters>({});
  const filterSetId = ref<Nullable<number>>(null);
  const loading = ref(false);
  const loadingIds = ref(false);
  const loadingError = ref(false);
  const mentions = ref<Mention[]>([]);
  const mentionIds = ref<number[]>([]);
  const compositeIds = ref<SyndicationKey[]>([]);
  const totalPages = ref(DEFAULT_NUMBER_OF_PAGES);
  const totalMentions = ref(0);
  const hasReachedEndOfMentions = ref(false);
  const lastRefreshedRange = ref<Nullable<DateRange>>(null);

  const syndicationStore = useSyndicationStore();

  const { currentUser, organisation } = storeToRefs(useUserStore());
  const { clearMentionsCount } = useMentionCountsStore();

  const {
    getSyndicationKeysForStream,
    resetSyndicationKeys,
    updateSyndicationKeys,
  } = syndicationStore;

  const rangeLimit = computed(() => {
    const maxDateRangeValue =
      organisation.value.max_date_range || getContentDateCap.value;

    const timestamp =
      maxDateRangeValue.toString().length >= 10
        ? maxDateRangeValue
        : dateToTimestamp(
            startOfDay(subtractTime(new Date(), maxDateRangeValue, "month"))
          );

    const lowestAllowedDate = dateToTimestamp(
      getLowestSearchableDate(new Date())
    );

    return timestamp > lowestAllowedDate ? timestamp : lowestAllowedDate;
  });

  function resetMentionsState(mentionsOptions: MentionsOptions): void {
    mentions.value = [];
    resetSyndicationKeys({ stream: stream.value });

    // We need a timeout because otherwise "stream" watchers in wordCloud and sentiment widget
    // Are called only one time when changing filters (with onlyLastSeenUpdated)
    // Instead of two, one without onlyLastSeenUpdated and one with
    // So they are not updated
    if (stream.value.id && mentionsOptions.clearMentionsCount) {
      setTimeout(
        () =>
          clearMentionsCount({
            ...stream.value,
            resetByGroup: false,
          }),
        100
      );
    }

    if (mentionsOptions.paginate) {
      page.value = mentionsOptions.page || 1;
    }
  }

  function getSearchRange(mentionsOptions: MentionsOptions): DateRange {
    const autoRefresh =
      currentUser.value.auto_refresh_streams ||
      (isSocialStream(stream.value) && socialAutoRefreshEnabled);

    let lastRefreshedAt = dateToTimestamp(
      (!autoRefresh &&
        !mentionsOptions.forceReload &&
        stream.value.refreshed_at) ||
        new Date()
    );

    // always load the latest bookmarks
    if (stream.value.type === streamTypes.bookmarkStream) {
      lastRefreshedAt = dateToTimestamp(new Date());
    }

    if (mentionsOptions.loadLatestMentions) {
      lastRefreshedAt = dateToTimestamp(new Date());
    }

    const fallbackRange = defaultRange.value;
    const rangeInDays = fallbackRange.range?.daysBack;

    const rangeFromLastRefresh =
      fallbackRange.range && rangeInDays
        ? {
            after: dateToTimestamp(
              subtractTime(
                startOfDay(lastRefreshedAt * 1000),
                rangeInDays,
                "day"
              )
            ),
            before: lastRefreshedAt,
          }
        : fallbackRange;

    const range = {
      ...(requestFilters.value?.range ||
        mentionsOptions.range ||
        rangeFromLastRefresh),
    };

    let nextAfter = range.after ?? fallbackRange.after;
    let nextBefore = range.before ?? fallbackRange.before;

    if (nextAfter < rangeLimit.value) {
      nextAfter = rangeLimit.value;
      if (rangeInDays)
        nextBefore = rangeLimit.value + DAY_IN_SECONDS * rangeInDays;
    }

    const currentDate = dateToTimestamp(new Date());
    if (!allowFutureContent.value && nextBefore > currentDate)
      nextBefore = currentDate;

    return new DateRange({
      after: nextAfter,
      before: nextBefore,
    });
  }

  function setFilters(
    filters: StreamRequestFilters = {}
  ): StreamRequestFilters {
    requestFilters.value = filters;

    return requestFilters.value;
  }

  function setFilterSetId(id: Nullable<number>): Nullable<number> {
    filterSetId.value = id;

    return filterSetId.value;
  }

  function getRequestParams(mentionsOptions: MentionsOptions): RequestParams {
    const params: RequestParams = {
      ...mentionsOptions,
      range: getSearchRange(mentionsOptions),
      syndicationKeys: getSyndicationKeysForStream(stream.value.id),
      timeout: REQUEST_TIMEOUT_MS,
    };

    if (params.paginate) {
      const paginateOptions = {
        limit: params.limit || pageSize.value,
        page: params.page || page.value,
      };

      Object.assign(params, { ...paginateOptions });
    }

    if (Object.keys(requestFilters.value).length) {
      params.search = requestFilters.value;
    }

    if (filterSetId.value) {
      params.filterSetId = filterSetId.value;
    }

    const sortOptions = params.sortingOptions
      ?.filter((option) => option.sortBy && option.orderBy)
      .map((option) => ({
        missing: option.missing,
        sort_by: option.sortBy!,
        sort_order: option.orderBy!,
      }));

    if (sortOptions && sortOptions.length > 0) {
      params.sort_options = sortOptions;
    }

    const excludedKeys: string[] = [
      "fetchingService",
      "defaultRange",
      "transformMentions",
      "sortingOptions",
      "paginate",
    ];

    excludedKeys.forEach((key) => delete params[key as keyof RequestParams]);

    if (stream.value.type === streamTypes.bookmarkStream) {
      params.includeBookmarkSyndication =
        includeBookmarkSyndication.value as boolean;
    }

    return params;
  }

  function processAndSetMentions(
    rawMentions: Mention[],
    mentionsOptions: MentionsOptions
  ): void {
    const processedMentions: Mention[] =
      mentionsOptions.transformMentions!(rawMentions) || [];

    if (
      mentionsOptions.keepAlreadyLoadedMentions &&
      !mentionsOptions.loadLatestMentions
    ) {
      mentions.value.push(...processedMentions);
    } else if (
      mentionsOptions.keepAlreadyLoadedMentions &&
      mentionsOptions.loadLatestMentions
    ) {
      const mentionIdList = mentions.value.map((mention) => mention.id);

      const newMentions = processedMentions.filter(
        (mention) =>
          !mentionIdList.includes(mention.id) &&
          mention.timestamp_milliseconds >
            mentions.value[0].timestamp_milliseconds
      );

      if (newMentions.length) {
        mentions.value.unshift(...newMentions);
        // remove the same number of mentions we're adding so that we avoid
        // Out Of Memory Error in the browser when auto refreshing for a
        // long period of time.
        mentions.value.splice(newMentions.length * -1, newMentions.length);

        setTimeout(() => clearMentionsCount(stream.value), 100);
      }
    } else {
      mentions.value = processedMentions;
    }

    if (!mentionsOptions.paginate) {
      updateSyndicationKeys({
        mentions: processedMentions,
        options: mentionsOptions,
        stream: stream.value,
      });
    }
  }

  async function loadMentionIds(
    loadOptions: MentionsOptions = {}
  ): Promise<void> {
    if (!stream.value) return;
    if (loadingIds.value) return;

    loadingIds.value = true;

    const mentionsOptions = { ...defaultRequestOptions.value, ...loadOptions };
    const requestParams = getRequestParams(mentionsOptions);

    try {
      const { data } = await fetchingService.value.getIds({
        mentions: mentions.value,
        options: {
          ...requestParams,
          limit: loadOptions.pageSize || undefined,
          page: loadOptions.page || 1,
        },
        stream: stream.value,
      });

      const rawIds: SyndicationKey[] = data || [];

      compositeIds.value = rawIds;
      mentionIds.value = rawIds.map((id) => compositeIdToMention(id).id);
    } catch (error) {
      ErrorDialog(
        getLocaleText("request_mentions.error_loading_mention_ids"),
        error
      );
    } finally {
      loadingIds.value = false;
    }
  }

  const mentionsAbortController = ref<AbortController>();

  async function loadMentions(loadOptions: MentionsOptions = {}) {
    if (!stream.value.id && !stream.value.skipReload) return;

    if (mentionsAbortController.value && loading.value) {
      mentionsAbortController.value.abort();
    }

    mentionsAbortController.value = new AbortController();

    if (!loadOptions.silent) {
      loading.value = true;
    }

    loadingError.value = false;

    const mentionsOptions = { ...defaultRequestOptions.value, ...loadOptions };

    if (
      (!mentionsOptions.keepAlreadyLoadedMentions ||
        mentionsOptions.forceReload) &&
      !mentionsOptions.silent
    ) {
      resetMentionsState(mentionsOptions);
    }

    const requestParams = getRequestParams(mentionsOptions);

    lastRefreshedRange.value = new DateRange(requestParams.range as DateRange);

    try {
      const { data, headers } = await fetchingService.value.get({
        mentions: mentions.value,
        options: {
          ...requestParams,
          signal: mentionsAbortController.value.signal,
        },
        stream: stream.value,
      });

      // Check if request was aborted before updating state
      if (!mentionsAbortController.value.signal.aborted) {
        processAndSetMentions(data, mentionsOptions);

        totalMentions.value =
          Number(headers["x-total-count"]) || mentions.value.length;

        totalPages.value =
          Number(headers["x-total-pages"]) || DEFAULT_NUMBER_OF_PAGES;

        const currentPage =
          Number(headers["x-page"]) || requestParams.page || page.value;

        // `collapse_syndicated` folds duplicates away after the page has been
        // sliced, so a page can come back shorter than the requested limit
        // while later pages still hold mentions. When paginating, only the
        // pagination headers can tell us we've reached the end.
        hasReachedEndOfMentions.value = mentionsOptions.paginate
          ? currentPage >= totalPages.value
          : data.length < (requestParams.limit || DEFAULT_PAGE_SIZE);

        if (infiniteScroll.value) {
          if (
            hasReachedEndOfMentions.value &&
            !mentionsOptions.loadLatestMentions
          ) {
            infiniteScroll.value.stop();
          } else {
            infiniteScroll.value.resume();
          }
        }

        loading.value = false;
      }
    } catch (error: any) {
      if (isAbortError(error)) {
        return;
      }

      if (infiniteScroll.value) infiniteScroll.value.stop();
      loading.value = false;
      loadingError.value = true;
    }
  }

  async function loadPage(
    pageNumber: number,
    loadOptions: MentionsOptions = {}
  ): Promise<boolean> {
    if (loading.value) return false;

    await loadMentions({
      keepAlreadyLoadedMentions: false,
      page: pageNumber,
      ...loadOptions,
    });

    return true;
  }

  async function loadPreviousPage(
    loadOptions: MentionsOptions = {}
  ): Promise<void> {
    if (totalPages.value > 1 && page.value > 1) {
      page.value -= 1;
      await loadPage(page.value, loadOptions);
    }
  }

  async function loadNextPage(
    loadOptions: MentionsOptions = {}
  ): Promise<void> {
    if (totalPages.value > 1 && page.value + 1 <= totalPages.value) {
      page.value += 1;
      await loadPage(page.value, loadOptions);
    }
  }

  async function loadLatestMentions(): Promise<boolean> {
    if (loading.value) return false;

    await loadMentions({
      keepAlreadyLoadedMentions: true,
      limit: AUTO_REFRESH_MENTIONS_LIMIT,
      loadLatestMentions: true,
      paginate: true,
    });

    return true;
  }

  async function refresh(loadOptions: MentionsOptions = {}): Promise<void> {
    await loadMentions({
      keepAlreadyLoadedMentions: false,
      ...loadOptions,
    });
  }

  async function handlePaginationChange(newPagination: {
    page: number;
    pageSize: number;
  }): Promise<void> {
    if (newPagination.pageSize !== pageSize.value) {
      pageSize.value = newPagination.pageSize;

      await loadMentions({
        keepAlreadyLoadedMentions: false,
        page: 1,
        pageSize: newPagination.pageSize,
      });
    } else if (newPagination.page !== page.value) {
      await loadPage(newPagination.page);
    }
  }

  watch(
    () => currentUser.value.currency_code,
    () => {
      refresh();
    }
  );

  return {
    compositeIds,
    handlePaginationChange,
    hasReachedEndOfMentions,
    lastRefreshedRange,
    loading,
    loadingError,
    loadingIds,
    loadLatestMentions,
    loadMentionIds,
    loadMentions,
    loadNextPage,
    loadPage,
    loadPreviousPage,
    mentionIds,
    mentions,
    mentionsAbortController,
    page,
    pageSize,
    rangeLimit,
    refresh,
    setFilters,
    setFilterSetId,
    totalMentions,
    totalPages,
  };
}
